'use client';

import * as React from 'react';
import { motion } from 'motion/react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@workspace/ui/components/tabs';

import { AccountMark } from './account-intelligence-scenes';

type WatchView = 'activity' | 'messaging';
type SceneProps = { reduced: boolean; ready: boolean };

const ACCOUNT_ACTIVITY = [
  {
    account: 'Quillstack',
    relationship: 'Your target account',
    observation: 'CEO Maya Chen commented on the demo.',
    source: 'Public comment',
    action: 'Review the outreach angle.',
    portrait: 'https://i.pravatar.cc/120?img=47',
    person: 'Maya Chen'
  },
  {
    account: 'Brightvault',
    relationship: 'Your customer',
    observation: 'Two contacts engaged with Fieldnote’s content.',
    source: 'CRM + public activity',
    action: 'Ask the owner to review.',
    portrait: null,
    person: null
  },
  {
    account: 'Cedarpath',
    relationship: 'Their customer',
    observation: 'Named in a Fieldnote customer story.',
    source: 'Public customer story',
    action: 'Research displacement fit.',
    portrait: null,
    person: null
  }
] as const;

const ARRIVALS = [0.35, 0.85, 1.35];

export function CompetitiveWatchScene({
  reduced,
  ready
}: SceneProps): React.JSX.Element {
  const [view, setView] = React.useState<WatchView>('activity');
  const [visited, setVisited] = React.useState({
    activity: reduced,
    messaging: reduced
  });
  const [hovered, setHovered] = React.useState<number | null>(null);
  const compositionRef = React.useRef<HTMLDivElement>(null);
  const competitorRef = React.useRef<HTMLSpanElement>(null);
  const evidenceRef = React.useRef<HTMLDivElement>(null);
  const findingRef = React.useRef<HTMLElement>(null);
  const accountRefs = React.useRef<Array<HTMLSpanElement | null>>([]);
  const [connections, setConnections] = React.useState({
    width: 1,
    height: 1,
    paths: [] as string[]
  });

  React.useEffect(() => {
    if (!ready && !reduced) return;
    setVisited((current) =>
      current[view] ? current : { ...current, [view]: true }
    );
  }, [ready, reduced, view]);

  React.useLayoutEffect(() => {
    const composition = compositionRef.current;
    const origin =
      view === 'activity' ? competitorRef.current : evidenceRef.current;
    const targets =
      view === 'activity' ? accountRefs.current : [findingRef.current];
    if (!composition || !origin) return;

    // Offset coordinates are unaffected by the parent preview's entrance or tilt.
    const position = (element: HTMLElement): { x: number; y: number } => {
      let x = 0;
      let y = 0;
      let current: HTMLElement | null = element;
      while (current && current !== composition) {
        x += current.offsetLeft;
        y += current.offsetTop;
        current = current.offsetParent as HTMLElement | null;
      }
      return { x, y };
    };

    const measure = (): void => {
      const source = position(origin);
      const paths = targets.map((target) => {
        if (!target) return '';
        const destination = position(target);
        const horizontal = destination.x > source.x + origin.offsetWidth;
        if (horizontal) {
          const startX = source.x + origin.offsetWidth;
          const startY = source.y + origin.offsetHeight / 2;
          const endX = destination.x;
          const endY = destination.y + target.offsetHeight / 2;
          const curve = Math.max((endX - startX) * 0.5, 16);
          return `M ${startX} ${startY} C ${startX + curve} ${startY}, ${endX - curve} ${endY}, ${endX} ${endY}`;
        }

        const startX = source.x + origin.offsetWidth / 2;
        const startY = source.y + origin.offsetHeight;
        const endX = destination.x + target.offsetWidth / 2;
        const endY = destination.y;
        const midY = startY + (endY - startY) * 0.5;
        return `M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`;
      });
      setConnections({
        width: composition.clientWidth,
        height: composition.clientHeight,
        paths
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(composition);
    observer.observe(origin);
    targets.forEach((target) => {
      if (target) observer.observe(target);
    });
    return () => observer.disconnect();
  }, [view]);

  const started = reduced || visited[view];

  return (
    <Tabs
      value={view}
      activationMode="manual"
      onValueChange={(value) => {
        setView(value as WatchView);
        setHovered(null);
      }}
      data-competitive-watch
      className="flex h-full min-h-0 gap-0"
    >
      <TabsList
        aria-label="Competitive watch views"
        onKeyDown={(event) => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.stopPropagation();
          }
        }}
        className="mx-3 flex h-11 shrink-0 justify-start gap-4 rounded-none border-b border-[color:var(--hairline-2)] bg-transparent p-0 sm:mx-6 md:mx-8 md:gap-6"
      >
        {[
          { value: 'activity', label: 'Account activity' },
          { value: 'messaging', label: 'Messaging & traction' }
        ].map((item) => (
          <TabsTrigger
            key={item.value}
            value={item.value}
            className="relative h-full min-w-0 flex-none rounded-none border-0 border-b-2 border-transparent bg-transparent px-0 text-[10.5px] font-medium text-[color:var(--ink-4)] shadow-none transition-colors duration-200 hover:text-[color:var(--ink)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--accent-deep)] data-[state=active]:border-[color:var(--accent-deep)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--accent-darker)] data-[state=active]:shadow-none min-[375px]:text-[11px] md:text-[12px]"
          >
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <div
        ref={compositionRef}
        className="relative grid min-h-0 flex-1 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] gap-3 px-3 pb-3 pt-4 sm:px-6 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)] md:grid-rows-1 md:items-center md:gap-14 md:px-8 md:pb-5 md:pt-3"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-25 [background-image:radial-gradient(circle,rgba(36,166,137,0.15)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
        />
        <svg
          aria-hidden
          viewBox={`0 0 ${connections.width} ${connections.height}`}
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 size-full overflow-visible"
        >
          {connections.paths.map((path, index) => (
            <motion.path
              key={`${view}-${index}`}
              d={path}
              fill="none"
              stroke="var(--accent-deep)"
              strokeWidth={hovered === index ? 1.5 : 1}
              vectorEffect="non-scaling-stroke"
              initial={reduced || visited[view] ? false : { pathLength: 0 }}
              animate={{
                pathLength: started ? 1 : 0,
                opacity:
                  hovered === null ? 0.28 : hovered === index ? 0.65 : 0.13
              }}
              transition={{
                pathLength: {
                  duration: reduced ? 0 : 0.65,
                  delay:
                    reduced || !started
                      ? 0
                      : view === 'activity'
                        ? ARRIVALS[index]! - 0.2
                        : 1.35
                },
                opacity: { duration: reduced ? 0 : 0.2 }
              }}
            />
          ))}
        </svg>

        <div className="relative z-10 min-w-0 md:self-center">
          <div className="flex items-center gap-2.5 md:gap-3">
            <span
              ref={competitorRef}
              aria-hidden
              className="relative grid size-9 shrink-0 place-items-center rounded-[11px] bg-[color:var(--ink-2)] font-serif text-[25px] italic leading-none text-[color:var(--paper)] shadow-[0_5px_14px_rgba(24,39,30,0.08)] md:size-12 md:rounded-[14px] md:text-[34px]"
            >
              f.
            </span>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold tracking-[-0.02em] text-[color:var(--ink)] md:text-[19px]">
                Fieldnote
              </p>
              <p className="mt-0.5 text-[10px] leading-snug text-[color:var(--ink-4)]">
                Competitor
              </p>
            </div>
          </div>

          {view === 'activity' && (
            <p className="mt-2 max-w-[230px] text-[10px] leading-relaxed text-[color:var(--ink-4)] md:mt-3">
              Public activity, not confirmed buying intent.
            </p>
          )}

          <div
            ref={evidenceRef}
            hidden={view !== 'messaging'}
            className="relative mt-4"
          >
            <MessagingEvidence
              reduced={reduced}
              ready={visited.messaging}
            />
          </div>
        </div>

        <TabsContent
          value="activity"
          forceMount
          className="relative z-10 m-0 min-h-0 self-start outline-none data-[state=inactive]:hidden md:self-center"
        >
          <ol
            aria-label="Competitor activity across your accounts"
            className="m-0 list-none p-0"
          >
            {ACCOUNT_ACTIVITY.map((activity, index) => (
              <li
                key={activity.account}
                onPointerEnter={(event) => {
                  if (event.pointerType === 'mouse') setHovered(index);
                }}
                onPointerLeave={() => setHovered(null)}
                className="relative border-b border-[color:var(--hairline-2)] last:border-0"
              >
                <article className="flex min-h-[117px] gap-2.5 py-2.5 md:min-h-[94px] md:gap-3">
                  <span
                    ref={(node) => {
                      accountRefs.current[index] = node;
                    }}
                    className="relative mt-0.5 grid size-8 shrink-0 place-items-center"
                  >
                    <ActivityIdentity index={index} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <p className="text-[12px] font-semibold tracking-[-0.01em] text-[color:var(--ink)] md:text-[13px]">
                        {activity.account}
                      </p>
                      <motion.p
                        initial={reduced ? false : { opacity: 0, y: 3 }}
                        animate={{
                          opacity: reduced || visited.activity ? 1 : 0,
                          y: reduced || visited.activity ? 0 : 3
                        }}
                        transition={{
                          duration: reduced ? 0 : 0.3,
                          delay: reduced ? 0 : ARRIVALS[index]! + 0.35
                        }}
                        className="text-[10px] font-medium leading-snug text-[color:var(--accent-darker)]"
                      >
                        {activity.relationship}
                      </motion.p>
                    </div>
                    <p className="mt-1 text-[11px] leading-[1.45] text-[color:var(--ink-3)] md:text-[11.5px]">
                      {activity.observation}
                    </p>
                    <p className="mt-1 text-[10px] leading-[1.4] text-[color:var(--ink-4)]">
                      {activity.source}
                    </p>
                    <p className="mt-1.5 text-[10.5px] font-medium leading-[1.4] text-[color:var(--accent-darker)]">
                      {activity.action}
                    </p>
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </TabsContent>

        <TabsContent
          value="messaging"
          forceMount
          className="relative z-10 m-0 min-h-0 self-center outline-none data-[state=inactive]:hidden"
        >
          <article
            ref={findingRef}
            className="relative isolate border-l-2 border-[color:var(--accent-deep)] bg-[color:var(--mint)]/35 px-4 py-4 md:px-5 md:py-6"
          >
            <motion.span
              aria-hidden
              initial={reduced ? false : { opacity: 0 }}
              animate={{ opacity: reduced || visited.messaging ? 0.4 : 0 }}
              transition={{
                duration: reduced ? 0 : 0.65,
                delay: reduced ? 0 : 1.85
              }}
              className="pointer-events-none absolute inset-0 -z-10 bg-[color:var(--mint)]"
            />
            <p className="text-[10px] font-medium text-[color:var(--accent-darker)] md:text-[11px]">
              Opportunity to test
            </p>
            <h3 className="mt-2 max-w-[260px] text-[19px] font-semibold leading-[1.2] tracking-[-0.025em] text-[color:var(--ink)] md:text-[24px]">
              Address migration effort and downtime.
            </h3>
          </article>
        </TabsContent>
      </div>
    </Tabs>
  );
}

function ActivityIdentity({ index }: { index: number }): React.JSX.Element {
  const activity = ACCOUNT_ACTIVITY[index]!;
  const [photoFailed, setPhotoFailed] = React.useState(false);

  return (
    <>
      {index === 0 ? (
        <AccountMark />
      ) : (
        <span
          aria-hidden
          className={`grid size-8 place-items-center rounded-[9px] text-[color:var(--accent-darker)] ring-1 ring-inset ring-[color:var(--hairline)] ${index === 1 ? 'bg-[color:var(--mint)]' : 'bg-[color:var(--paper-2)]'}`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="size-[18px]"
          >
            {index === 1 ? (
              <path d="M6 17V9l6-4 6 4v8M6 13l6 4 6-4M12 5v12" />
            ) : (
              <path d="m12 4 7 12H5L12 4Zm0 5v11M8 20h8" />
            )}
          </svg>
        </span>
      )}
      {activity.portrait && (
        <span
          aria-hidden
          className="absolute -bottom-1.5 -right-1.5 grid size-[18px] place-items-center overflow-hidden rounded-full bg-[color:var(--mint)] font-mono text-[7px] text-[color:var(--accent-darker)] ring-2 ring-[color:var(--paper)]"
        >
          {activity.person
            ?.split(' ')
            .map((part) => part[0])
            .join('')}
          {!photoFailed && (
            <img
              src={activity.portrait}
              alt=""
              width={18}
              height={18}
              onError={() => setPhotoFailed(true)}
              className="absolute inset-0 size-full object-cover saturate-[0.8]"
            />
          )}
        </span>
      )}
    </>
  );
}

function MessagingEvidence({ reduced, ready }: SceneProps): React.JSX.Element {
  const visible = reduced || ready;
  return (
    <div className="relative space-y-2.5 md:space-y-3">
      <motion.figure
        initial={reduced ? false : { opacity: 0, y: 7 }}
        animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 7 }}
        transition={{ duration: reduced ? 0 : 0.45 }}
        className="relative m-0 rounded-[12px] border border-[color:var(--hairline)] bg-[color:var(--paper)] p-3 shadow-[0_7px_22px_rgba(15,45,32,0.05)] md:p-4"
      >
        <figcaption className="mb-2 text-[10px] text-[color:var(--ink-4)]">
          Company post
        </figcaption>
        <blockquote className="m-0 text-[17px] font-medium leading-[1.3] tracking-[-0.02em] text-[color:var(--ink)] md:text-[19px]">
          “Switch platforms in a day.”
        </blockquote>
      </motion.figure>
      <div className="ml-4 space-y-2 border-l border-[color:var(--hairline)] pl-3">
        {[
          'How much migration work is involved?',
          'What happens to downtime?'
        ].map((reply, index) => (
          <motion.blockquote
            key={reply}
            initial={reduced ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: visible ? 1 : 0, y: visible ? 0 : 6 }}
            transition={{
              duration: reduced ? 0 : 0.4,
              delay: reduced ? 0 : 0.4 + index * 0.4
            }}
            className="m-0 text-[11px] leading-relaxed text-[color:var(--ink-3)]"
          >
            “{reply}”
          </motion.blockquote>
        ))}
        <p className="text-[9px] text-[color:var(--ink-4)]">Audience replies</p>
      </div>
    </div>
  );
}
